import { useMemo } from "react";
import { useFaultEvents, type FaultEvent } from "./useFaultEvents";
import { useManualFaultEvents, type ManualFaultEvent } from "./useManualFaultEvents";
import * as io from "io-ts";

export type EventType = "DIAGNOSED" | "MANUAL";
export const EventType = io.union([io.literal("DIAGNOSED"), io.literal("MANUAL")]);
type EventMap = {
  DIAGNOSED: FaultEvent;
  MANUAL: ManualFaultEvent;
};
export type TypedEvent<T extends EventType> = EventMap[T] & {
  type: T;
  failureNames: string[];
};

export function useEvents(projectId: string, enabled: boolean) {
  const faultEvents = useFaultEvents(projectId).events;
  const manualFaultEvents = useManualFaultEvents(projectId, enabled);

  const typedFaultEvents: TypedEvent<"DIAGNOSED">[] = useMemo(
    () =>
      faultEvents.map(faultEvent => ({
        ...faultEvent,
        type: "DIAGNOSED",
      })),
    [faultEvents],
  );

  const typedManualFaultEvents: TypedEvent<"MANUAL">[] = useMemo(
    () =>
      manualFaultEvents.map(manualFaultEvent => ({
        ...manualFaultEvent,
        failureNames: [manualFaultEvent.metadata.manualFault.name],
        type: "MANUAL",
      })),
    [manualFaultEvents],
  );

  return useMemo(
    () => [...typedFaultEvents, ...typedManualFaultEvents],
    [typedFaultEvents, typedManualFaultEvents],
  );
}

export type Event = ReturnType<typeof useEvents>[number];
export type EventDiagnosed = Extract<Event, { type: "DIAGNOSED" }>;
export type ManualEvent = Extract<Event, { type: "MANUAL" }>;
